import React, { useMemo, useState } from "react";
import ReactSelect from "react-select";
import FormActions from "../../../components/Form/FormActions";
import { COLORS } from "../../../constants";
import { Tamanho } from "../../../services/tamanhoService";
import { toast } from "react-toastify";

interface RodizioModalProps {
  tamanhos: Tamanho[];
  isLoadingTamanhos: boolean;
  isSavingDoce: boolean;
  onCancel: () => void;
  onSubmitDoce: (tamanhoId: number, quantidade: number) => void;
  onStartSalgadoTimer: (tamanhoId: number, tamanhoNome: string) => void;
}

const RodizioModal: React.FC<RodizioModalProps> = ({
  tamanhos,
  isLoadingTamanhos,
  isSavingDoce,
  onCancel,
  onSubmitDoce,
  onStartSalgadoTimer,
}) => {
  const [selectedTamanhoId, setSelectedTamanhoId] = useState<number | null>(
    null
  );
  const [quantidade, setQuantidade] = useState("1");

  const options = useMemo(
    () =>
      [...tamanhos]
        .sort((a, b) =>
          a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
        )
        .map((tamanho) => ({
          value: tamanho.id,
          label: tamanho.nome,
        })),
    [tamanhos]
  );

  const selectedOption =
    options.find((option) => option.value === selectedTamanhoId) || null;
  const isDoceSelected = selectedOption
    ? selectedOption.label.toLowerCase().includes("doce")
    : false;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedTamanhoId) {
      toast.error("Selecione um tamanho de rodízio.");
      return;
    }

    if (!isDoceSelected) {
      onStartSalgadoTimer(
        selectedTamanhoId,
        selectedOption?.label || "Rodízio"
      );
      return;
    }

    const quantidadeNumber = Number(quantidade);
    if (!Number.isInteger(quantidadeNumber) || quantidadeNumber <= 0) {
      toast.error("Informe uma quantidade válida.");
      return;
    }

    onSubmitDoce(selectedTamanhoId, quantidadeNumber);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700">
          Tamanho do rodízio
        </label>
        <ReactSelect
          options={options}
          placeholder={
            isLoadingTamanhos ? "Carregando tamanhos..." : "Selecione"
          }
          isLoading={isLoadingTamanhos}
          value={selectedOption}
          onChange={(option) => setSelectedTamanhoId(option?.value || null)}
          styles={{
            control: (base) => ({
              ...base,
              borderColor: COLORS.NEUTRAL_300,
              "&:hover": {
                borderColor: COLORS.PRIMARY,
              },
              "&:focus-within": {
                borderColor: COLORS.PRIMARY,
                boxShadow: "0 0 0 2px rgba(147, 32, 24, 0.2)",
              },
            }),
          }}
        />
        {!isLoadingTamanhos && options.length === 0 && (
          <p className="mt-2 text-sm text-neutral-500">
            Nenhum tamanho de rodízio encontrado para a sua loja.
          </p>
        )}
      </div>

      {selectedTamanhoId && isDoceSelected && (
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Quantidade
          </label>
          <input
            type="number"
            min={1}
            step={1}
            value={quantidade}
            onChange={(event) => setQuantidade(event.target.value)}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Ex: 10"
            required
          />
        </div>
      )}

      {selectedTamanhoId && !isDoceSelected && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
          Ao confirmar, o timer de rodízio salgado será iniciado.
        </div>
      )}

      <FormActions
        onCancel={onCancel}
        isLoading={isSavingDoce}
        submitText={isDoceSelected ? "Registrar Rodízio" : "Iniciar Timer"}
      />
    </form>
  );
};

export default RodizioModal;
